import { DEFAULT_BY_ID, slugifyId } from "./frameworkDefaults";
import {
  GATE0_REGISTRY,
  canonicalTextForGate0Field,
  gate0FieldForCanonicalText,
  isTextCanonicalForGate0Field,
  reconcileItemGate0Fields,
} from "./gate0Registry";
import { isSectionRequiredForIntake, sectionRequiredFromItems } from "../../utils/chatUtils";

export {
  GATE0_REGISTRY,
  canonicalTextForGate0Field,
  gate0FieldForCanonicalText,
  isTextCanonicalForGate0Field,
  reconcileItemGate0Fields,
} from "./gate0Registry";

/** App Identity field labels for gate0-mapped questions. */
export const GATE0_FIELD_LABELS = {
  app_name: "App Name",
  app_version: "Version",
  owner_name: "Application Owner",
  owner_role: "Business Unit",
  environment: "Environment",
  criticality: "Criticality",
  intended_use: "Short Description",
  archetype: "Archetype",
  tool_calling: "Tool Calling",
  mcp_connected: "MCP Connected",
  multi_agent: "Multi-Agent",
  guardrail_layer: "Guardrail Layer",
  output_validation: "Output Validation",
  data_classification: "Data Classification",
  frameworks: "Frameworks",
  eu_ai_act_high_risk: "EU AI Act High Risk",
  autonomy_scope: "Autonomy Scope",
  oversight_level: "Oversight Level",
  domain: "Business Domain",
};

/** True when this question is pre-filled from the App Identity screen via gate0_field. */
export function isGate0MappedQuestion(item) {
  const field = item?.gate0_field;
  return field != null && String(field).trim() !== "";
}

export function gate0FieldLabel(field) {
  if (!field) return "App Identity";
  return GATE0_FIELD_LABELS[field] || String(field).replace(/_/g, " ");
}

/** Default question text from the standard framework (App Identity link baseline). */
export function canonicalGate0QuestionText(sectionId, itemId) {
  const base = DEFAULT_BY_ID[sectionId]?.items?.find((i) => i.id === itemId);
  return (base?.text || "").trim();
}

/** Drop App Identity link when question text no longer matches the standard default. */
export function reconcileGate0FieldAfterMerge(merged, baseItem) {
  if (!baseItem?.gate0_field) return merged;
  const canonical = (baseItem.text || "").trim();
  const current = (merged.text || "").trim();
  if (current === canonical) return merged;
  const next = { ...merged };
  delete next.gate0_field;
  if (next.extraction_key === baseItem.extraction_key) {
    delete next.extraction_key;
  }
  return next;
}

/** Keep gate0_field when canonical App Identity question text is unchanged (any section). */
export function shouldKeepGate0Mapping(item, sectionId) {
  const text = (item.text || "").trim();
  if (gate0FieldForCanonicalText(text)) return true;
  if (isGate0MappedQuestion(item)) {
    return isTextCanonicalForGate0Field(text, item.gate0_field);
  }
  return false;
}

/** Section confidence threshold — stored and shown as % (10–100), same as intake engine. */

export function normalizeThresholdPercent(value, fallback = 85) {
  const n = Number(value);
  if (Number.isNaN(n)) return fallback;
  return Math.min(100, Math.max(10, Math.round(n)));
}

export function formatThresholdPercent(threshold) {
  return `${normalizeThresholdPercent(threshold)}%`;
}

export function cloneSectionForDrawer(section) {
  const items = (section.items || []).map((it) => ({
    id: it.id,
    text: it.text || "",
    required: it.required !== false,
    extraction_key: it.extraction_key,
    gate0_field: it.gate0_field,
  }));
  return {
    id: section.id,
    name: section.name || "",
    threshold: normalizeThresholdPercent(section.threshold ?? 85),
    required: isSectionRequiredForIntake({ ...section, items }),
    items,
  };
}

export function drawerDraftToSectionPatch(draft, originalId) {
  const sectionId = originalId || draft.id;
  const items = draft.items.map((it, i) => {
    const text = it.text.trim() || `Question ${i + 1}`;
    const item = {
      id: it.id || `q${i + 1}`,
      text,
      required: !!it.required,
    };
    if (shouldKeepGate0Mapping(it, sectionId)) {
      const field = it.gate0_field || gate0FieldForCanonicalText(text);
      if (field) {
        item.gate0_field = field;
        item.extraction_key =
          it.extraction_key || GATE0_REGISTRY.byField[field]?.extraction_key || slugifyId(text);
      }
    } else {
      item.extraction_key = slugifyId(text);
    }
    return item;
  });
  const allQuestionsMandatory =
    items.length > 0 && items.every((it) => it.required);

  return {
    id: originalId || draft.id,
    name: draft.name.trim() || "New Section",
    threshold: normalizeThresholdPercent(draft.threshold ?? 85),
    required: sectionRequiredFromItems(items),
    items,
    items_count: items.length,
  };
}
